<template>
  <section
    class="calendar-overview"
    data-cy="calendar-view"
    :data-view-ready="!loadingCalendarData">
    <template v-if="loadingCalendarData">
      <div
        class="col-12 mt-5 text-center">
        <coozzy-spinner />
      </div>
    </template>
    <template v-else>
      <div
        class="row mb-2 calendar-header-row">
        <div class="col-12 col-md-9 col-lg-10 big-calendar-container">
          <div class="row mb-md-2">
            <div :class="isScheduleViewingMode && object ? 'col-12 col-md-7' : 'col-md-6'">
              <title-marketing-object-view
                v-if="object"
                :object="object"
                :show-marketing-info-box="false"
                :title-prefix="isScheduleViewingMode ? $tc('message.marketingObjects.viewingAppointments.scheduleTitle') : ''" />
              <h1
                v-else-if="contacts.length > 0">
                {{ getNameMainContact() }}
              </h1>
              <coozzy-page-title
                v-else
                :class-title="'mb-0'"
                :title="$tc('message.navigation.calendar.title')"
                :count="countEvents"
                :count-text="showCalendar ? $t('message.generic.events').toString() : ''" />
            </div>
            <div
              v-if="showConnectCalendarButton && !showCalendar"
              class="col-12 mt-3">
              <coozzy-button
                data-cy="calendar-connect-button"
                class="ml-2 pl-5 pr-5"
                design="prop-green"
                @click="connectCalendar()">
                {{ $tc("message.calendar.connectCalendar") }}
              </coozzy-button>
            </div>
            <div
              v-else-if="showCalendar"
              :class="isScheduleViewingMode && object ? 'col-12 col-md-5' : 'col-md-6'"
              class="pt-4 btn-container">
              <coozzy-button
                data-cy="calendar-filter-today-button"
                size="small"
                class="float-md-right mb-2 ml-md-4 today-btn"
                @click="switchView(selectedView, new Date())">
                {{ $t('message.navigation.calendar.today') }}
              </coozzy-button>
              <b-button-group class="float-md-right shadow">
                <coozzy-button
                  data-cy="calendar-filter-agenda-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'list' ? 'primary': 'default'"
                  @click="switchView('list')">
                  {{ $t('message.navigation.calendar.list') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="calendar-filter-day-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'day' ? 'primary': 'default'"
                  @click="switchView('day');hideWeekends=false">
                  {{ $t('message.navigation.calendar.day') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="calendar-filter-workweek-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'week' && hideWeekends ? 'primary': 'default'"
                  @click="hideWeekends=true;switchView('week');">
                  {{ $t('message.navigation.calendar.workWeek') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="calendar-filter-week-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'week' && !hideWeekends ? 'primary': 'default'"
                  @click="hideWeekends=false;switchView('week');">
                  {{ $t('message.navigation.calendar.week') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="calendar-filter-month-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'month' ? 'primary': 'default'"
                  @click="switchView('month');hideWeekends=false">
                  {{ $t('message.navigation.calendar.month') }}
                </coozzy-button>
                <coozzy-button
                  data-cy="calendar-filter-year-button"
                  size="small"
                  class="shadow-none mb-0"
                  :design="selectedView === 'year' ? 'primary': 'default'"
                  @click="switchView('year');hideWeekends=false">
                  {{ $t('message.navigation.calendar.year') }}
                </coozzy-button>
              </b-button-group>
            </div>
          </div>
        </div>
        <div
          v-if="showCalendar"
          class="col-12 col-md-3 col-lg-2 px-md-0 small-calendar-container">
          <div class="row">
            <div class="col pt-3 pt-md-4 mb-3">
              <coozzy-button
                v-if="allCalendars.length > 0 && !isScheduleViewingMode"
                data-cy="calendar-create-event-button"
                class="w-100"
                design="prop-green"
                @click="createEvent(new Date())">
                {{ $t('message.calendar.createEvent') }}
              </coozzy-button>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="isScheduleViewingMode && (object || objectId)"
        class="row mb-2">
        <div class="col-12">
          <schedule-viewing-appointments-form
            :object-id="objectId || object.id"
            :active-users="scheduleActiveUsers"
            :viewing-contact="scheduleViewingContact"
            :current-user-id="currentUserId"
            :add-more="isScheduleAddMoreMode"
            @cancel="onScheduleViewingCancel"
            @saved="onScheduleViewingSaved"
            @draft-slots-changed="onScheduleDraftSlotsChanged"
            @focus-date-changed="onScheduleFocusDateChanged" />
        </div>
      </div>
      <template
        v-if="showCalendar">
        <div class="row calendar-body-row">
          <div
            data-cy="calendar-main-container"
            class="col-12 col-md-9 col-lg-10 big-calendar-container"
            @mousemove="onMouseMove($event)">
            <vue-cal
              v-if="selectedView !== 'list'"
              id="globalCal"
              :key="'global-' + selectedView"
              :ref="'globalCal'"
              class="shadow"
              :locale="getCurrentLanguage"
              :on-event-click="onEventClick"
              :events="calendarEvents"
              :hide-view-selector="true"
              :active-view.sync="selectedView"
              :show-week-numbers="true"
              :events-on-month-view="true"
              :selected-date="dayFocus"
              :events-count-on-year-view="true"
              :deletable="false"
              :dblclick-to-navigate="false"
              :cell-click-hold="false"
              :disable-views="['years']"
              :show-all-day-events="true"
              :time-from="6 * 60"
              :time-to="22 * 60"
              :hide-weekends="hideWeekends"
              :time-cell-height="timeCellHeight"
              @cell-click="clickOnCell"
              @cell-dblclick="createEvent"
              @day-focus="onDayFocus($event)"
              @view-change="onViewChange(selectedView, $event)"
              @ready="onCalReady()"
              @event-mouse-leave="mouseLeaveEvents()"
              @event-mouse-enter="mouseEnterEvents($event)">
              <template #event="{ event, view }">
                <div
                  v-if="view !== 'year' "
                  data-cy="calendar-event-entry"
                  class="container-event-month"
                  :class="getEventStatus(event)">
                  <div
                    v-dompurify-html="getFormattedEventTitle(event)"
                    class="event-title d-block" />
                  <div
                    v-if="view === 'month'"
                    class="vuecal__event-time d-block">
                    <!-- Using Vue Cal Date prototypes (activated by default) -->
                    <span>{{ event.start.formatTime() }}</span> {{ $t('message.generic.until') }}
                    <span>{{ event.end.formatTime() }}</span>
                  </div>
                </div>
              </template>
              <template #cell-content="content">
                <span
                  v-if="content.view.id === 'month'"
                  class="vuecal__cell-date"
                  style="position: absolute;
                    top: 1px;
                    text-align: left;
                    left: 5px;">
                  {{ content.cell.content }}
                </span>
                <span
                  v-if="content.view.id === 'year'"
                  class="container_cell_year_info"
                  @click="content.goNarrower">
                  <span
                    class="vuecal__cell-date"
                    :class="content.view.id">
                    {{ content.cell.content }}
                  </span>
                  <span
                    v-if="countYearEvents(content) > 0"
                    class="vuecal__cell-events-count">{{ countYearEvents(content) }}</span>
                </span>
                <span
                  v-if="['week', 'day'].includes(content.view.id) && !content.events.length && !isAllDayCell(content.cell)"
                  class="vuecal__no-event">
                  {{ $t('message.calendar.noEvent') }}
                </span>
              </template>
            </vue-cal>
            <div v-else>
              <h4
                v-if="eventByDate.length === 0"
                class="col-12 mt-5 pl-0 text-center text-lg">
                {{ $t('message.calendar.noFutureEvent') }}
              </h4>
              <template v-else>
                <div
                  v-for="(group, key) in eventByDate"
                  :key="key">
                  <coozzy-card
                    :title="getDayNameWithDate(group[1][0].start)"
                    class="cursor-pointer">
                    <b-table
                      data-cy="calendar-agenda-table"
                      hover
                      responsive
                      stacked="md"
                      bordered
                      class="mb-0 mt-2"
                      :fields="fields"
                      :items="sortedGroup(group[1])"
                      @row-clicked="onEventAgendaClick">
                      <template
                        #head(title)>
                        <span>{{ $t('message.calendar.event.title') }}</span>
                      </template>
                      <template
                        #head(time)>
                        <span>{{ $t('message.calendar.event.time') }}</span>
                      </template>
                      <template
                        #head(location)>
                        <span>{{ $t('message.calendar.event.location') }}</span>
                      </template>
                      <template
                        #head(description)>
                        <span>{{ $t('message.calendar.event.description') }}</span>
                      </template>
                      <template
                        #head(attendees)>
                        <span>{{ $t('message.calendar.event.participants') }}</span>
                      </template>
                      <template
                        #head(from)>
                        <span>{{ $t('message.calendar.event.from') }}</span>
                      </template>
                      <template
                        #cell(title)="data">
                        {{ data.item.originalTitle }}
                      </template>
                      <template
                        #cell(time)="data">
                        <template v-if="data.item.allDay">
                          {{ $t('message.calendar.event.allDay') }}
                        </template>
                        <template v-else>
                          {{ data.item.start.substr(11, 5) }} - {{ data.item.end.substr(11, 5) }}
                        </template>
                      </template>
                      <template
                        #cell(description)="data">
                        <div
                          v-dompurify-html="sanitizeHtml(data.item.description)"
                          class="m-0" />
                      </template>
                      <template
                        #cell(attendees)="data">
                        <div
                          v-for="(contact, index) in data.item.attendees"
                          :key="index">
                          <span v-if="contact.displayName !== ''">{{ contact.displayName }}</span>
                          <span v-else>{{ contact.email }}</span>
                          <span v-if="index !== data.item.attendees.length - 1">, </span>
                        </div>
                      </template>
                      <template
                        #cell(from)="data">
                        <div
                          class="color-box mx-2 agenda"
                          :style="{ background: getColor(data.item.color) }" />
                        {{ data.item.organizer?.name || data.item.organizer?.email }}
                      </template>
                    </b-table>
                  </coozzy-card>
                </div>
              </template>
            </div>
          </div>
          <div
            data-cy="calendar-mini-calendar-container"
            class="col-12 col-md-3 col-lg-2 my-4 my-md-0 px-md-0 small-calendar-container">
            <vue-cal
              :key="'small-' + viewCalendar"
              class="shadow"
              :locale="getCurrentLanguage"
              :on-event-click="onEventClick"
              :events="calendarEvents"
              :selected-date="dayFocus"
              hide-view-selector
              :active-view="'month'"
              :disable-views="['years', 'year', 'week', 'day']"
              xsmall
              @cell-click="onDayClick($event)"
              @cell-focus="onDayFocus($event)"
              @ready="onCalReady()" />
            <div
              data-cy="calendar-user-filters"
              class="employees-calendar shadow p-2 mt-3">
              <div
                class="row">
                <div class="col-9">
                  <h6 class="mb-3">
                    {{ $t('message.navigation.calendar.title') }}
                  </h6>
                </div>
                <div
                  class="col-3">
                  <coozzy-form-checkbox
                    ref="AllCheckbox"
                    data-cy="calendar-user-filter-all-checkbox"
                    @change="(value) => { selectAll(value) }" />
                </div>
              </div>
              <template v-if="queryParamsProcessed && employeeList.length > 0">
                <div
                  v-for="employee in sortedEmployeeList"
                  :key="employee.id">
                  <div
                    class="row">
                    <div class="contact-content col-9">
                      <div
                        class="color-box mx-2 mt-0"
                        :style="{ background: getColor(employee.profile.color) }" />
                      <label>
                        {{ employee.profile.lastName }} {{ employee.profile.firstName }}
                      </label>
                    </div>
                    <div
                      class="col-3"
                      style="margin-top: -2px;">
                      <coozzy-form-checkbox
                        ref="checkbox"
                        data-cy="calendar-user-filter-checkbox"
                        :initial="selectedCalendars.includes(employee.email.toLowerCase())"
                        @change="employeeSelected(employee)" />
                    </div>
                  </div>
                </div>
              </template>
              <coozzy-button
                v-if="showConnectCalendarButton"
                data-cy="calendar-connect-button"
                class="w-100 mt-2"
                design="default"
                @click="connectCalendar">
                {{ $tc("message.calendar.connectCalendar") }}
              </coozzy-button>
            </div>
          </div>
        </div>
      </template>
    </template>
    <event-creation-modal
      v-if="selectedEvent !== null || showCreationModal"
      ref="modal-creation"
      :key="keyId + (selectedEvent !== null ? 'event-modal-creation-'+ selectedEvent?.id : 'event-modal-creation' + defaultDate)"
      :event-detail="selectedEvent"
      :is-all-day="isAllDay"
      :default-date="defaultDate"
      :source="fromPage"
      :reference-id="$store.getters['user/getAccountId'].toString()"
      :available-employees="availableEmployees"
      :available-calendars="allCalendars"
      :contacts-list="contacts"
      :additional-references="referenceIds"
      :object-reference="object"
      :user-contact="userContact"
      :start-date-occurrences-response="computedStartDateOccurrencesResponse"
      :end-date-occurrences-response="computedEndDateOccurrencesResponse"
      @post-creation="postCreation"
      @remove-event="removeEvent"
      @hide-modal="closeDetailEvent"
      @attendees-changed="afterEdit"
      @reload-events="filterUpdatedEvents" />
    <b-modal
      v-if="showCreateCalendarModal"
      :id="'create-calendar-modal'"
      :ref="'create-calendar-modal'"
      :title="$tc('message.calendar.selectCalendar')">
      <div class="col-12 col-sm-12 mb-2">
        <label for="selectingNewCalendar">{{ $tc('message.navigation.calendar.title') }} </label>
        <coozzy-form-select
          id="selectingNewCalendar"
          v-model="calendarForCreation">
          <option
            v-for="(item, index) in availableCalendars"
            :key="index"
            :value="item.nylasCalendarId"
            :disabled="calendarOfUser && item.nylasCalendarId === calendarOfUser.nylasCalendarId">
            {{ item.name }}
          </option>
        </coozzy-form-select>
      </div>
      <template #modal-footer>
        <div class="w-100 float-right">
          <coozzy-button
            design="green"
            class="float-right ml-3"
            :disabled="calendarForCreation === '' || savingCalendar"
            @click="setCalendar()">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
          <coozzy-button
            design="default"
            :disabled="savingCalendar"
            @click="hideCreateCalendarModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <div
      v-if="showPopover"
      id="eventPopover"
      data-cy="calendar-event-popover"
      class="eventPopover"
      :style="{ left: page.left + 'px', top: page.top + 'px'}">
      <div
        v-if="selectedPopOverEvent"
        class="row">
        <div class="content shadow p-3 mt-3">
          <div
            class="row mb-2">
            <div class="col-12 m-negative-5">
              <span class="font-weight-bold">
                {{
                  selectedPopOverEvent.start.toLocaleDateString('de-DE', {
                    weekday: 'short',
                    year: '2-digit',
                    month: '2-digit',
                    day: '2-digit'
                  })
                }}<template v-if="!selectedPopOverEvent.allDay">,</template>
                <template v-if="!selectedPopOverEvent.allDay">
                  {{ selectedPopOverEvent.start.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'}) }}
                  -
                  {{ selectedPopOverEvent.end.toLocaleTimeString('de-DE', {hour: '2-digit', minute: '2-digit'}) }}
                </template>
              </span>
            </div>
            <div class="col-12 m-negative-5">
              <span class="font-weight-bold">
                {{ selectedPopOverEvent.originalTitle }}
              </span>
            </div>
          </div>
          <div
            v-if="(!selectedPopOverEvent.private || selectedPopOverEvent.userId === currentUserId) && selectedPopOverEvent.contentFull !== '-'"
            class="row mb-2">
            <div class="col-12 text-85 m-negative-5">
              {{ $t('message.calendar.event.description') }}:
            </div>
            <!-- <div class="col-12 text-85 m-negative-5 description-text">
              {{ getNWordsFromString(selectedPopOverEvent.contentFull, 30, true) }}
            </div> -->
            <div
              v-dompurify-html="sanitizeHtml(selectedPopOverEvent.contentFull)"
              class="col-12 text-85 m-negative-5 description-text" />
          </div>
          <div
            v-if="(!selectedPopOverEvent.private || selectedPopOverEvent.userId === currentUserId) && selectedPopOverEvent.attendees.length > 0"
            class="row mb-2">
            <div class="col-12 text-85 m-negative-5">
              <p class="mt-2 mb-0">
                {{ $t('message.calendar.event.participants') }}:
              </p>
            </div>
            <div
              v-for="attendee in selectedPopOverEvent.attendees"
              :key="attendee.email"
              class="col-12 text-85 m-negative-5">
              <img
                v-if="attendee.status === 'ATTENDEE_STATUS_NEEDS_ACTION'"
                width="10px"
                height="10px"
                src="@/assets/icon/hourglass.svg"
                :alt="'hourglass'">
              <coozzy-success-icon v-if="attendee.status === 'ATTENDEE_STATUS_ACCEPTED'" />
              <coozzy-fail-icon v-if="attendee.status === 'ATTENDEE_STATUS_DECLINED'" />
              <span>
                {{ attendee.displayName !== '' ? attendee.displayName : attendee.email }}
              </span>
            </div>
          </div>
          <div
            v-if="(!selectedPopOverEvent.private || selectedPopOverEvent.userId === currentUserId) && selectedPopOverEvent.location !== '-'"
            class="row">
            <div class="col-12 text-85 m-negative-5">
              {{ $t('message.calendar.event.location') }}: {{ selectedPopOverEvent.location }}
            </div>
          </div>
          <div
            v-if="!selectedPopOverEvent.allDay"
            class="row">
            <div class="col-12 text-85 m-negative-5">
              {{ $t('message.tableColumnHeadings.duration') }}: {{ calculateDuration(selectedPopOverEvent) }}h
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-12 text-85 m-negative-5">
              {{ $t('message.navigation.calendar.title') }}:
              <div
                class="color-box text d-inline-block ml-1"
                :style="{
                  background: getColor(selectedPopOverEvent.color),
                  color: pickTextColorBasedOnBgColorSimple(getColor(selectedPopOverEvent.color))
                }">
                {{
                  selectedPopOverEventOrganizer?.lastName?.substring(0, 1)
                }}{{ selectedPopOverEventOrganizer?.firstName?.substring(0, 1) }}
              </div>
              {{ selectedPopOverEventOrganizer?.lastName }}
              {{ selectedPopOverEventOrganizer?.firstName }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import VueCal from 'vue-cal'
import 'vue-cal/dist/vuecal.css'
import 'vue-cal/dist/i18n/de.es.js'
import 'vue-cal/dist/i18n/it.es.js'
import 'vue-cal/dist/i18n/fr.es.js'
import EventCreationModal from './EventCreationModal.vue'
import Vue from 'vue'
import CoozzyButton from '../../framework/components/button/CoozzyButton.vue'
import CoozzyCard from '../../framework/components/card/CoozzyCard.vue'
import CoozzyFormCheckbox from '../../framework/components/form/checkbox/CoozzyFormCheckbox.vue'
import CoozzyPageTitle from '../../framework/layout/CoozzyPageTitle.vue'
import ObjectApi from '../../misc/apis/ObjectApi'
import ContactApi from '../../misc/apis/ContactApi'
import { routeChecks } from '@/mixins/routeChecks'
import TitleMarketingObjectView from '@/marketing/TitleMarketingObjectView.vue'
import ScheduleViewingAppointmentsForm from '@/marketing/components/ScheduleViewingAppointmentsForm.vue'
import RequestApi from '@/marketingBasic/api/RequestApi'
import { user } from '@/mixins/user'
import { mapActions, mapGetters } from 'vuex'
import { calendar } from '@/mixins/calendar'
import CoozzySuccessIcon from '@/framework/components/icons/CoozzySuccessIcon.vue'
import CoozzyFailIcon from '@/framework/components/icons/CoozzyFailIcon.vue'
import { responsivity } from '@/mixins/responsivity'
import { dateUtils } from '@/mixins/dateUtils'
import { language } from '@/mixins/language'
import { eventBus } from '@/mixins/mail'
import CalendarApi from '@/misc/apis/CalendarApi'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { NylasCalendar, TypeDate } from '@/types/api'
import { CalendarContact } from '@/types/models'
import { Dictionary } from 'vue-router/types/router'

export type LoadedCalendarRange = {
    rangeStart: TypeDate | null
    rangeEnd: TypeDate | null
}

export default Vue.extend({
  name: 'CalendarOverview',
  components: {
    CoozzyFormSelect,
    CoozzySpinner,
    CoozzyPageTitle,
    TitleMarketingObjectView,
    ScheduleViewingAppointmentsForm,
    CoozzyFormCheckbox,
    CoozzyCard,
    CoozzyButton,
    VueCal,
    EventCreationModal,
    CoozzySuccessIcon,
    CoozzyFailIcon
  },
  mixins: [user, routeChecks, calendar, responsivity, dateUtils, language],
  beforeRouteUpdate(to, from, next) {
    if (to.name === from.name) {
      if (to.query.elementSelected && to.query.elementSelected !== '') {
        this.changeStatingEndingDate(
          this.startDate && !isNaN(Date.parse(this.startDate))
            ? new Date(this.startDate)
            : to.query.startDate && typeof to.query.startDate === 'string' && !isNaN(Date.parse(to.query.startDate))
              ? new Date(to.query.startDate)
              : new Date(),
          this.selectedView,
          true
        )
        this.loadEvents(true, to.query.elementSelected, to.query.startDate)
        return
      }
    }
    next()
  },
  props: {
    dayToFocus: {
      type: [Date, String],
      default: function () {
        return ''
      }
    },
    viewCalendar: {
      type: String,
      default: 'week'
    },
    calendars: {
      type: String,
      default: ''
    },
    referenceIds: {
      type: String,
      default: ''
    },
    objectId: {
      type: String,
      default: ''
    },
    elementSelected: {
      type: String,
      default: ''
    },
    fromPage: {
      type: String,
      default: ''
    },
    startDate: {
      type: String,
      default: ''
    }
  },
  metaInfo() {
    return {
      title: this.$t('message.navigation.calendar.title')
    }
  },
  data() {
    return {
      loadExtremeEvent: false,
      startDateFilter: null as TypeDate | null,
      endDateFilter: null as TypeDate | null,
      firstLoad: false,
      isInitialLoad: true, // Add this flag to track initial load
      isPostCreationFocusActive: false,
      countEvents: 0,
      showPopover: true,
      hideWeekends: localStorage.getItem('hideWeekends') ? localStorage.getItem('hideWeekends') === 'true' : true,
      keyId: '',
      page: {
        left: 0,
        top: 0
      },
      object: null as null | { id: string, category?: string, name?: string, contacts?: Record<string, unknown>, viewingAppointment?: Record<string, unknown> },
      contacts: [] as CalendarContact[],
      scheduleViewingContact: null as null | {
        id: string
        displayName: string
        roleLabel: string
        email: string
        contactType: string
      },
      dayFocus: new Date(),
      events: [],
      scheduleDraftSlots: [],
      dvEvents: [],
      selectedEvent: null,
      selectedPopOverEvent: null,
      showDialog: false,
      defaultDate: null as Date | null,
      fetchedMonth: [] as string[],
      queryParamsProcessed: false,
      selectedCalendars: [] as string[],
      selectedView: 'week',
      fields: [
        {
          key: 'time',
          label: 'time',
          thClass: 'table-header',
          tdClass: 'align-middle time-cell'
        },
        {
          key: 'title',
          label: 'title',
          thClass: 'table-header',
          tdClass: 'align-middle title-cell'
        },
        {
          key: 'description',
          label: 'description',
          thClass: 'table-header',
          tdClass: 'align-middle description-cell'
        },
        {
          key: 'location',
          label: 'location',
          thClass: 'table-header',
          tdClass: 'align-middle location-cell'
        },
        {
          key: 'attendees',
          label: 'attendees',
          thClass: 'table-header',
          tdClass: 'align-middle attendees-cell'
        },
        {
          key: 'from',
          label: 'from',
          thClass: 'table-header',
          tdClass: 'align-middle from-cell'
        }
      ],
      isAllDay: false,
      loadedRangesByCalendars: {} as LoadedCalendarRange,
      userContact: null as CalendarContact | null,
      windowHeight: window.innerHeight,
      headerHeight: 0,
      calendarHeaderHeight: 0,
      showCreationModal: false,
      availableCalendars: [] as NylasCalendar[],
      allCalendars: [] as NylasCalendar[],
      calendarOfUser: undefined as NylasCalendar | undefined,
      showCreateCalendarModal: false,
      calendarForCreation: '',
      savingCalendar: false,
      loadingCalendarData: true,
      modalHideHandler: null as ((bvEvent: unknown, modalId: string) => void) | null,
      updateEventsListHandler: null as (() => void) | null
    }
  },
  computed: {
    showConnectCalendarButton() {
      return !this.calendarOfUser
    },
    showCalendar() {
      return this.allCalendars.length > 0
    },
    isScheduleViewingMode() {
      return this.fromPage === 'scheduleViewing' || this.$route.query.fromPage === 'scheduleViewing'
    },
    isScheduleAddMoreMode() {
      return this.isScheduleViewingMode &&
        (this.$route.query.addMore === '1' || this.$route.query.addMore === 'true')
    },
    scheduleDraftEvents() {
      return this.scheduleDraftSlots.map(slot => {
        const dateObj = this.toObject(slot.date)
        const startTime = {
          hours: parseInt(slot.from.split(':')[0], 10),
          minutes: parseInt(slot.from.split(':')[1], 10),
          seconds: 0,
          nanos: 0
        }
        const endTime = {
          hours: parseInt(slot.to.split(':')[0], 10),
          minutes: parseInt(slot.to.split(':')[1], 10),
          seconds: 0,
          nanos: 0
        }
        const start = this.formatDateForCalendar(dateObj, startTime)
        const end = this.formatDateForCalendar(dateObj, endTime)
        const organizerEmployee = this.employeeList.find(employee => employee.id === slot.calendarUserId)
        const color = organizerEmployee?.profile?.color || ''
        return {
          id: slot.id,
          userId: slot.calendarUserId,
          title: slot.title,
          originalTitle: this.decodeHtmlEntitiesIfNeeded(slot.title),
          start,
          end,
          startDate: dateObj,
          endDate: dateObj,
          startTime,
          endTime,
          allDay: false,
          private: false,
          attendees: [],
          location: '-',
          contentFull: '-',
          description: '',
          class: `colorful-event color-${this.getColor(color)} schedule-draft-event`,
          color,
          isScheduleDraft: true
        }
      })
    },
    calendarEvents() {
      const selectedUserIds = this.employeeList
        .filter(employee => this.selectedCalendars.includes(employee.email?.toLowerCase()))
        .map(employee => employee.id)
      const drafts = selectedUserIds.length > 0
        ? this.scheduleDraftEvents.filter(e => selectedUserIds.includes(e.userId))
        : this.scheduleDraftEvents
      const baseEvents = this.events.filter(e => !e.isScheduleDraft)
      return baseEvents.concat(drafts)
    },
    timeCellHeight() {
      // Subtract the heights of the headers from windowHeight before dividing by 17
      const value = (this.windowHeight - this.headerHeight - this.calendarHeaderHeight) / 17
      return value > 50 ? value : 50
    },
    computedStartDateOccurrencesResponse() {
      return this.startDateFilter
    },
    computedEndDateOccurrencesResponse() {
      return this.endDateFilter
    },
    employeeList() {
      if (this.getEmployees().length > 0) {
        return this.getEmployees().filter(e => !e.deleted && !e.blocked)
      }
      return []
    },
    daysOftheWeek() {
      return [this.$t('message.generic.daysComplete.sunday'),
        this.$t('message.generic.daysComplete.monday'),
        this.$t('message.generic.daysComplete.tuesday'),
        this.$t('message.generic.daysComplete.wednesday'),
        this.$t('message.generic.daysComplete.thursday'),
        this.$t('message.generic.daysComplete.friday'),
        this.$t('message.generic.daysComplete.saturday')]
    },
    sortedEmployeeList() {
      if (this.employeeList.length === 0) {
        return []
      }
      const currentUser = this.calendarOfUser ? this.employeeList.filter(employee => employee.id === this.userId) : []
      let sortedEmployees = this.allCalendars.length > 0
        ? this.employeeList.filter(employee => employee.id !== this.userId && this.allCalendars.find(cal => cal.userId === employee.id))
        : []
      sortedEmployees = sortedEmployees.sort(function (a, b) {
        return (a.profile.lastName.localeCompare(b.profile.lastName))
      })
      return currentUser.concat(sortedEmployees)
    },
    currentUserId() {
      return this.$store.getters['user/getUserId']
    },
    eventByDate() {
      const eventsByDate = {}
      let upcomingEventData = []
      this.events.forEach((event) => {
        const startDate = new Date(event.start.substr(0, 10))
        const endDate = new Date(event.end.substr(0, 10))
        const differenceInDays = this.getDifferenceInDays(startDate, endDate)
        if (differenceInDays > 1 && event.allDay) {
          for (let index = 0; index < differenceInDays; index++) {
            const eventData = { ...event }
            const eventOccurrenceDate = new Date(eventData.start.substr(0, 10))
            eventOccurrenceDate.setDate(eventOccurrenceDate.getDate() + index)
            eventData.startDate = eventOccurrenceDate instanceof Date
              ? {
                day: eventOccurrenceDate.getDate(),
                month: eventOccurrenceDate.getMonth() + 1,
                year: eventOccurrenceDate.getFullYear()
              }
              : null

            const todayStart = new Date()
            todayStart.setHours(0, 0, 0, 0)
            const eventStartDateTime = new Date(this.formatDateForCalendar(eventData.startDate, eventData.startTime))
            if (eventStartDateTime >= todayStart) {
              upcomingEventData.push(eventData)
            }
          }
        } else {
          upcomingEventData.push(event)
        }
      })
      upcomingEventData = upcomingEventData.filter((event, index, self) =>
          index === self.findIndex((e) =>
            e.id === event.id &&
            e.start.substr(0, 10) === event.start.substr(0, 10) &&
            e.end.substr(0, 10) === event.end.substr(0, 10)
          )
      )
      upcomingEventData.forEach((event) => {
        const eventDate = event.start.substr(0, 10)
        if (eventDate in eventsByDate) {
          eventsByDate[eventDate].push(event)
        } else {
          eventsByDate[eventDate] = new Array(event)
        }
      })
      const groupedEvents = []
      for (const dateKey in eventsByDate) {
        const today = new Date()
        today.setHours(0, 0, 0, 0)
        const groupedDate = new Date(dateKey.replace('\\-', ' '))
        if (groupedDate.getTime() >= today.getTime()) {
          groupedEvents.push([dateKey, eventsByDate[dateKey]])
        }
      }
      return groupedEvents
    },
    availableEmployees() {
      const array = []
      if (this.sortedEmployeeList.length !== 0) {
        const empList = this.sortedEmployeeList.filter(x => (x.blocked === false && x.deleted === false))
        empList.forEach(element => {
          const employeeOption = {
            firstName: element.profile.firstName,
            lastName: element.profile.lastName,
            email: element.email,
            id: element.id
          }
          array.push(employeeOption)
        })
      }
      return array
    },
    scheduleActiveUsers() {
      return this.employeeList.map(element => ({
        firstName: element.profile?.firstName || '',
        lastName: element.profile?.lastName || '',
        email: element.email,
        id: element.id
      }))
    },
    getCurrentLanguage() {
      return this.$store.getters['internationalization/getCurrentLanguage']
    },
    selectedPopOverEventOrganizer() {
      if (this.selectedPopOverEvent) {
        return this.availableEmployees.find(employee => employee.id === this.selectedPopOverEvent.userId)
      }
      return null
    }
  },
  watch: {
    employeeList: {
      immediate: true,
      handler: function (newValue, oldValue) {
        if (this.employeeList.length > 0 && (this.firstLoad || (oldValue?.length === 0 && newValue?.length > 0))) {
          this.changeStatingEndingDate(this.getFocusDateForRange(), this.selectedView)
        }
      }
    },
    selectedCalendars: function (newVal) {
      this.updateURLCalendarView(newVal)
      if (newVal.length === 0) {
        this.dvEvents = []
        this.events = []
        return
      }
      // Immediately filter out events from unselected calendars
      const selectedUserIds = this.employeeList
        .filter(employee => newVal.includes(employee.email.toLowerCase()))
        .map(employee => employee.id)
      this.events = this.events.filter(e => selectedUserIds.includes(e.userId))
      this.dvEvents = this.dvEvents.filter(e => selectedUserIds.includes(e.userId))
      if (!this.startDateFilter && !this.endDateFilter) {
        this.changeStatingEndingDate(this.getFocusDateForRange(), this.selectedView)
      }
      this.$nextTick(function () {
        if (this.employeeList.length > 0) {
          this.loadEvents(true)
        }
      })
    },
    selectedView: function (newValue) {
      this.updateURLCalendarView(this.selectedCalendars)
      localStorage.setItem('lastUsedCalendarView', newValue)
      if (this.firstLoad) {
        eventBus.$emit('lastUsedCalendarView', newValue)
        this.$nextTick(() => {
          this.updateEventCounts()
        })
      }
    },
    hideWeekends: function (newValue) {
      localStorage.setItem('hideWeekends', newValue.toString())
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.modalHideHandler) {
      this.$root.$off('bv::modal::hide', this.modalHideHandler)
    }
    if (this.updateEventsListHandler) {
      eventBus.$off('updateEventsList', this.updateEventsListHandler)
    }
  },
  created() {
    this.dayFocus = this.normalizeDayToFocus(this.dayToFocus)
    this.updateEventsListHandler = () => {
      this.loadEvents(true)
    }
    eventBus.$on('updateEventsList', this.updateEventsListHandler)
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.modalHideHandler = (bvEvent, modalId) => {
      if (modalId === 'modal-add-event') {
        if (this.$route.query && this.$route.query.elementSelected) {
          const query = Object.assign({}, this.$route.query)
          delete query.elementSelected
          delete query.startDate
          this.$router.replace({ query })
        }
      }
    }
    this.$root.$on('bv::modal::hide', this.modalHideHandler)
    this.loadCalendarData()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    normalizeEmail(email) {
      return typeof email === 'string' ? email.toLowerCase() : ''
    },
    async loadCalendarData() {
      this.loadingCalendarData = true
      this.dayFocus = this.normalizeDayToFocus(this.dayToFocus)
      // Load data and map query params to internal variables
      this.selectedView = this.viewCalendar
      if (this.calendars) {
        this.selectedCalendars = [...new Set((this.calendars || '').split(',').filter(e => e).map(e => e.toLowerCase()))]
      } else {
        this.selectedCalendars = [this.userEmail.toLowerCase()]
      }
      this.queryParamsProcessed = true
      if (localStorage.getItem('lastUsedCalendarView')) {
        this.selectedView = localStorage.getItem('lastUsedCalendarView')
      }
      if (this.isScheduleViewingMode) {
        this.selectedView = 'week'
        this.hideWeekends = false
      }
      await this.loadEmployees()
      await this.loadCalendars()
      this.changeStatingEndingDate(this.getFocusDateForRange(), this.selectedView)
      await this.loadEvents()
      await this.loadUserContact()
      await this.handleResize()

      if (this.objectId !== '') {
        try {
          const response = await ObjectApi.getObject(this.objectId)
          this.object = response.object
          await this.resolveScheduleViewingContact()
        } catch (e) {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.object'), { type: 'error' })
        }
      }
      if (this.referenceIds !== '') {
        const allContacts = this.referenceIds.split(',').filter(x => x.startsWith('contact'))
        try {
          const response = await ContactApi.contactResolve(allContacts)
          this.contacts = ((response.persons as CalendarContact[])
            .concat(response.companies))
            .filter(x => allContacts.includes(x.id))
        } catch (e) {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        }
      }

      this.loadingCalendarData = false
      if (localStorage.getItem('startNylasAuth') === 'true') {
        localStorage.removeItem('startNylasAuth')
        await this.showCreateCalendar()
      }
    },
    connectCalendar() {
      const nameRoute = this.isOwnerModule ? 'OwnerSettingsView' : this.isAdminModule ? 'AdminSettingsView' : this.isAssetModule ? 'AssetSettingsView' : this.isAccountingModule ? 'AccountingSettingsView' : 'MarketingSettingsView'
      this.$router.push({ name: nameRoute, query: { view: 8 } })
    },
    async loadCalendars() {
      try {
        const response = await CalendarApi.listCalendarsByUserIds(this.employeeList.map(emp => emp.id))
        this.allCalendars = response.calendars || []
        this.calendarOfUser = this.allCalendars.find(cal => cal.userId === this.userId) || null
      } catch (e) {
        console.log(e)
        Vue.toasted.show(this.$t('message.loadingErrors.calendar'), { type: 'error' })
      }
    },
    async showCreateCalendar() {
      this.availableCalendars = (await CalendarApi.listAvailableCalendars(this.userId)).calendars
      this.showCreateCalendarModal = true
      this.calendarForCreation = this.calendarOfUser ? this.calendarOfUser.nylasCalendarId : ''
      this.$nextTick(() => {
        if (this.$refs['create-calendar-modal']) {
          this.$refs['create-calendar-modal'].show()
        }
      })
    },
    hideCreateCalendarModal() {
      if (this.$refs['create-calendar-modal']) {
        this.$refs['create-calendar-modal'].hide()
      }
      this.showCreateCalendarModal = false
      this.calendarForCreation = ''
    },
    async setCalendar() {
      this.savingCalendar = true
      const selectedCalendar = this.availableCalendars.find(cal => cal.nylasCalendarId === this.calendarForCreation)
      let newSelectedCalendars = []
      let hasOldCalendar = false
      if (this.calendarOfUser) {
        this.events = []
        this.dvEvents = []
        newSelectedCalendars = this.selectedCalendars.filter(cal => cal !== this.userEmail.toLowerCase())
        hasOldCalendar = true
      }
      CalendarApi.setCalendar({ nylasCalendarId: selectedCalendar.nylasCalendarId }).then(response => {
        this.savingCalendar = false
        this.calendarOfUser = response.calendar
        newSelectedCalendars.push(this.userEmail.toLowerCase())
        // this.loadEvents()
        if (hasOldCalendar) {
          Vue.toasted.show(this.$t('message.successMessages.calendarChanged'), { type: 'success' })
        } else {
          Vue.toasted.show(this.$t('message.successMessages.calendarCreation'), { type: 'success' })
        }
      }).catch(e => {
        console.log(e)
        this.savingCalendar = false
        Vue.toasted.show(this.$t('message.savingErrors.calendarCreation'), { type: 'error' })
      }).finally(() => {
        this.selectedCalendars = newSelectedCalendars
        this.hideCreateCalendarModal()
      })
    },
    countYearEvents(content) {
      // we use this method to get the event count in year view
      // because the all-day events are not supported in year view in vue-cal
      // source info: copilot
      let count = 0
      if (content) {
        const startDate = content.cell?.startDate ? new Date(content.cell.startDate).getTime() : null
        const endDate = content.cell?.endDate ? new Date(content.cell.endDate).getTime() : null
        if (content.view?.events?.length > 0 && startDate && endDate) {
          content.view.events.forEach(event => {
            const eventStart = new Date(event.start).getTime()
            const eventEnd = new Date(event.end).getTime()
            if (eventStart >= startDate && eventEnd <= endDate) {
              count++
            }
          })
        }
      }
      return count
    },
    handleResize() {
      this.windowHeight = window.innerHeight || 0
      // Get the heights of the headers
      const navBar = Array.from(document.getElementsByClassName('app-header navbar')) as HTMLElement[]
      const bigCalendarItems = Array.from(document.getElementsByClassName('big-calendar-container')) as HTMLElement[]
      let pageTitle = 0
      let calendarHeader = 0
      // calculate header (navbar) height
      if (navBar?.length > 0) {
        this.headerHeight = navBar[0].offsetHeight
      }
      // get the height of the page title
      if (bigCalendarItems?.length > 0) {
        pageTitle = bigCalendarItems[0].offsetHeight
      }
      // get the height of the calendar header
      if (bigCalendarItems?.length > 1) {
        const vueCalHeader = Array.from(bigCalendarItems[1].getElementsByClassName('vuecal__header')) as HTMLElement[]
        if (vueCalHeader.length > 0) {
          calendarHeader = vueCalHeader[0].offsetHeight
        }
      }
      // the 30px added is for the margin top of the page container
      this.calendarHeaderHeight = pageTitle + calendarHeader + 30
      this.$nextTick(() => {
        const vuecalBg = document.querySelector('.vuecal__bg') as HTMLElement
        const vuecalAllDay = document.querySelector('.vuecal__all-day') as HTMLElement

        if (vuecalBg && vuecalAllDay) {
          const overflowY = window.getComputedStyle(vuecalBg).getPropertyValue('overflow-y')
          if ((overflowY === 'scroll' || overflowY === 'auto') && vuecalBg.scrollHeight > vuecalBg.clientHeight) {
            vuecalAllDay.style.overflowY = 'scroll'
          } else {
            vuecalAllDay.style.overflowY = 'hidden'
          }
        }
      })
    },
    updateURLCalendarView(calendars) {
      let checkRoute = ''
      if (this.isOwnerModule) {
        checkRoute = 'OwnerCalendarOverview'
      } else if (this.isMarketingModule) {
        checkRoute = 'MarketingCalendarOverview'
      } else if (this.isAdminModule) {
        checkRoute = 'AdminCalendarOverview'
      } else if (this.isAccountingModule) {
        checkRoute = 'AccountingCalendarOverview'
      } else if (this.isAssetModule) {
        checkRoute = 'AssetCalendarOverview'
      }

      const joinedCalendars = calendars.join(',')

      if (checkRoute !== '') {
        const query: Dictionary<string | (string | null)[] | null | undefined> = { calendars: joinedCalendars }
        const dayToFocus = this.getDayToFocusQuery()
        if (dayToFocus) {
          query.dayToFocus = dayToFocus
        }
        const params = this.$route.params
        if (this.objectId !== '' || this.$route.query.objectId !== '') {
          query.objectId = this.objectId !== '' ? this.objectId : this.$route.query.objectId
        }
        if (this.referenceIds !== '' || this.$route.query.referenceIds !== '') {
          query.referenceIds = this.referenceIds ? this.referenceIds : this.$route.query.referenceIds
        }
        if (this.elementSelected !== '' || this.$route.query.elementSelected !== '') {
          query.elementSelected = this.elementSelected ? this.elementSelected : this.$route.query.elementSelected
        }
        if (this.fromPage !== '' || this.$route.query.fromPage !== '') {
          query.fromPage = this.fromPage ? this.fromPage : this.$route.query.fromPage
        }
        if (this.$route.query.addMore === '1' || this.$route.query.addMore === 'true') {
          query.addMore = '1'
        }
        if (this.selectedView !== '' || this.$route.query.viewCalendar !== '') {
          params.viewCalendar = this.selectedView ? this.selectedView : this.$route.query.viewCalendar
        }
        // do not change the link if there is no change in the URL
        const oldURL = this.$route.fullPath
        const newURL = this.$router.resolve({
          name: checkRoute,
          params: params,
          query: query
        }).href

        if (oldURL !== newURL) {
          this.$router.replace({
            name: checkRoute,
            params: params,
            query: query
          })
        }
      }
    },
    loadUserContact() {
      ContactApi.resolveByMail(this.accountId, [this.userEmail])
        .then(response => {
          if (response.persons.length > 0) {
            this.userContact = response.persons[0]
          } else if (response.companies.length > 0) {
            this.userContact = response.companies[0]
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    getEventStatus(event) {
      if (event.status === 'EVENT_STATUS_CANCELLED') {
        return 'cancelled'
      }
      if (event.attendees.length === 0 || event.userId !== this.currentUserId) {
        return 'accepted'
      } else {
        const associatedEmail = this.normalizeEmail(this.calendarOfUser?.associatedEmail)
        const nylasCalendarId = this.normalizeEmail(this.calendarOfUser?.nylasCalendarId)
        const attendee = event.attendees.find(x => {
          const attendeeEmail = this.normalizeEmail(x.email)
          return attendeeEmail !== '' && (attendeeEmail === associatedEmail || attendeeEmail === nylasCalendarId)
        })
        if (attendee) {
          if (attendee.status === 'ATTENDEE_STATUS_ACCEPTED') {
            return 'accepted'
          }
          if (attendee.status === 'ATTENDEE_STATUS_DECLINED') {
            return 'declined'
          } else {
            return ''
          }
        } else {
          return 'accepted'
        }
      }
    },
    updateEventCounts() {
      if (this.selectedView !== 'list') {
        const view = this.$refs.globalCal?.view
        if (view) {
          const start = view.startDate
          let end = view.endDate

          // Filter events based on the current view
          const filteredEvents = this.events.filter((event) => {
            const eventStart = new Date(event.start).getTime()
            return eventStart >= start.getTime() && eventStart <= end.getTime()
          })

          // Update count
          this.countEvents = filteredEvents.length
        } else {
          this.countEvents = 0
        }
      } else if (this.selectedView === 'list') {
        let sum = 0

        this.eventByDate.forEach(group => {
          const listEvents = group[1]
          sum += listEvents.length
        })

        this.countEvents = sum
      }
    },
    mouseLeaveEvents() {
      this.selectedPopOverEvent = null
      this.showPopover = true
    },
    mouseEnterEvents(event) {
      this.selectedPopOverEvent = event
      this.showPopover = true
    },
    onMouseMove(e) {
      if (document.getElementById('eventPopover')) {
        const tooltipWidth = document.getElementById('eventPopover').clientWidth
        const tooltipHeight = document.getElementById('eventPopover').clientHeight
        const windowWidth = window.innerWidth
        const windowHeight = window.innerHeight
        // Use clientX/clientY for viewport-relative positioning (matches position: fixed)
        if ((windowWidth - 100) >= (tooltipWidth + e.clientX)) {
          this.page.left = e.clientX
        } else {
          this.page.left = e.clientX - tooltipWidth
        }
        // Calculate available space above and below the cursor
        const headerHeight = 40
        const spaceAbove = e.clientY
        const spaceBelow = windowHeight - e.clientY
        // Show popover on the side with more available space
        if (spaceAbove >= spaceBelow) {
          // More space above, show popover above the cursor
          this.page.top = e.clientY - tooltipHeight
          // Ensure popover doesn't exceed the top of the page (below the header)
          if (this.page.top < headerHeight) {
            this.page.top = headerHeight
          }
        } else {
          // More space below, show popover below the cursor
          this.page.top = e.clientY
          // Ensure popover doesn't exceed the bottom of the page
          if (this.page.top + tooltipHeight > windowHeight) {
            this.page.top = windowHeight - tooltipHeight
            // If popover is still too tall for the viewport, clamp to bottom of header
            if (this.page.top < headerHeight) {
              this.page.top = headerHeight
            }
          }
        }
      }
    },
    calculateDuration(event) {
      return Math.round(((event.end.getTime() - event.start.getTime()) / 1000 / 3600) * 100) / 100
    },
    updateColors() {
      this.$nextTick(function () {
        const eventDomList = document.getElementsByClassName('colorful-event')
        for (let index = 0; index < eventDomList.length; index++) {
          const element = eventDomList[index]
          let classList = element.className.split(' ')
          classList = classList.filter(c => {
            return c.includes('color-')
          })
          const color = classList[0].replace('color-', '')
          let childAccepted = element.querySelector('.container-event-month.accepted') as HTMLElement
          if (childAccepted) {
            childAccepted.style.cssText += 'color: ' + this.pickTextColorBasedOnBgColorSimple(color) + ' !important;'
            childAccepted.style.cssText += 'background-color: ' + color + ' !important;'
            childAccepted.style.cssText += 'border-color: ' + color + ' !important;'
          } else {
            let child = element.querySelector('.container-event-month') as HTMLElement
            child.style.cssText += 'color: ' + color + ' !important;'
            child.style.cssText += 'background-color: white !important;'
            child.style.cssText += 'border-color: ' + color + ' !important;'
          }
        }
      })
    },
    pickTextColorBasedOnBgColorSimple(bgColor) {
      const color = (bgColor.charAt(0) === '#') ? bgColor.substring(1) : bgColor
      // If we have alpha value then make special check
      if (color.length > 6) {
        const a = parseInt(color.substring(6, 8), 16) // hexToB
        if (a < 125) {
          return '#000000'
        }
      }
      const r = parseInt(color.substring(0, 2), 16) // hexToR
      const g = parseInt(color.substring(2, 4), 16) // hexToG
      const b = parseInt(color.substring(4, 6), 16) // hexToB
      return (((r * 0.299) + (g * 0.587) + (b * 0.114)) > 186) ? '#000000' : '#ffffff'
    },
    isAllDayCell(cell) {
      const eventAll = this.events.filter(obj => obj.allDay && (obj.startDate.year + '-' + ('0' + obj.startDate.month).slice(-2) + '-' + ('0' + obj.startDate.day).slice(-2)) === cell.formattedDate)
      return eventAll.length > 0
    },
    clickOnCell($event) {
      this.dayFocus = this.formatDateForCalendar(this.toObject(($event)))
      this.updateURLCalendarView(this.selectedCalendars)
    },
    parseDateValue(value) {
      if (!value) {
        return null
      }
      if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate())
      }
      if (typeof value === 'string') {
        const parsedDate = new Date(value)
        if (!Number.isNaN(parsedDate.getTime())) {
          return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())
        }
      }
      return null
    },
    normalizeDayToFocus(value) {
      const parsedDate = this.parseDateValue(value)
      return parsedDate || new Date()
    },
    getFocusDateForRange() {
      return this.parseDateValue(this.dayFocus) || new Date()
    },
    getAnchorDateForEventLoad(startDate) {
      if (startDate) {
        return new Date(startDate)
      }
      if (this.startDate) {
        return new Date(this.startDate)
      }
      return this.getFocusDateForRange()
    },
    getDayToFocusQuery() {
      const parsedDate = this.parseDateValue(this.dayFocus)
      if (!parsedDate) {
        return null
      }
      const month = `${parsedDate.getMonth() + 1}`.padStart(2, '0')
      const day = `${parsedDate.getDate()}`.padStart(2, '0')
      return `${parsedDate.getFullYear()}-${month}-${day}`
    },
    changeStatingEndingDate(date, view, forceLoad) {
      let currentDate = null
      if (view === 'firstLoad') {
        if (date && (!forceLoad || new Date(date) > new Date())) {
          currentDate = date
        } else {
          const now = new Date()
          currentDate = new Date(now.getFullYear(), now.getMonth(), 1)
        }
        // Get the first day of the current month
        const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        // Get the last day of the current month
        const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)

        // Find Monday of the week containing the first day of month
        const firstDayWeekDay = firstDayOfMonth.getDay() // 0 = Sunday, 1 = Monday, etc.
        const mondayOfFirstWeek = new Date(firstDayOfMonth)
        mondayOfFirstWeek.setDate(firstDayOfMonth.getDate() - (firstDayWeekDay === 0 ? 6 : firstDayWeekDay - 1))

        // Go back one week from that Monday (start of week before)
        const weekBeforeStart = new Date(mondayOfFirstWeek)
        weekBeforeStart.setDate(mondayOfFirstWeek.getDate() - 7)

        // Find Sunday of the week containing the last day of month
        const lastDayWeekDay = lastDayOfMonth.getDay() // 0 = Sunday, 1 = Monday, etc.
        const sundayOfLastWeek = new Date(lastDayOfMonth)
        sundayOfLastWeek.setDate(lastDayOfMonth.getDate() + (lastDayWeekDay === 0 ? 0 : 7 - lastDayWeekDay))

        // Go forward one week from that Sunday (end of week after)
        const weekAfterEnd = new Date(sundayOfLastWeek)
        weekAfterEnd.setDate(sundayOfLastWeek.getDate() + 7)

        this.startDateFilter = this.toObject(weekBeforeStart)
        this.endDateFilter = this.toObject(weekAfterEnd)
      }
      if (view === 'day') {
        if (date && !forceLoad) {
          currentDate = date
        } else {
          currentDate = new Date()
        }
        this.startDateFilter = this.toObject(currentDate.addDays(-1))
        this.endDateFilter = this.toObject(currentDate.addDays(+1))
      }
      if (view === 'week') {
        if (date && !forceLoad) {
          currentDate = date
        } else {
          const now = new Date()
          const mondayTimeStamp = now.setDate(now.getDate() - now.getDay() + 1)
          currentDate = new Date(mondayTimeStamp)
        }
        this.startDateFilter = this.toObject(currentDate.addDays(-7))
        this.endDateFilter = this.toObject(currentDate.addDays(+14))
      }
      if (view === 'month') {
        if (date && (!forceLoad || new Date(date) > new Date())) {
          currentDate = date
        } else {
          const now = new Date()
          currentDate = new Date(now.getFullYear(), now.getMonth(), 1)
        }
        const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 0)
        const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        this.startDateFilter = this.toObject(previousMonth)
        this.endDateFilter = this.toObject(nextMonth)
      }
      if (view === 'year') {
        if (date && !forceLoad) {
          currentDate = date
        } else {
          const now = new Date()
          currentDate = new Date(now.getFullYear(), 0, 1)
        }
        const previousYear = new Date(currentDate.getFullYear(), 0, 1)
        this.startDateFilter = this.toObject(previousYear)
        const nextYear = new Date(currentDate.getFullYear() + 1, 0, 1)
        this.endDateFilter = this.toObject(nextYear)
      }
      if (view === 'list') {
        currentDate = new Date()
        const previousMonth = currentDate
        const nextMonth = (new Date()).setDate(currentDate.getDate() + 90)
        this.startDateFilter = this.toObject(previousMonth)
        this.endDateFilter = this.toObject(nextMonth)
        // Reset loaded range for current calendar combination to force reload for list view
        const calendarKey = this.getCalendarKey()
        if (this.loadedRangesByCalendars[calendarKey]) {
          this.loadedRangesByCalendars[calendarKey].rangeStart = null
          this.loadedRangesByCalendars[calendarKey].rangeEnd = null
        }
      }
      if (forceLoad) {
        this.loadEvents(true)
      }
    },
    filterUpdatedEvents(event = null) {
      if (event.masterEventId !== '') {
        this.dvEvents = this.dvEvents.filter(x => !x.masterEventId || x.masterEventId !== event.masterEventId)
        this.events = this.events.filter(x => !x.masterEventId || x.masterEventId !== event.masterEventId)
      } else {
        this.dvEvents = this.dvEvents.filter(x => x.id !== event.id)
        this.events = this.events.filter(x => x.id !== event.id)
      }
      this.loadEvents(true)
    },
    async loadEvents(forceRefresh = false, eventId = '', startDate = '') {
      // Early exit if no calendars are selected
      if (!this.selectedCalendars.length) return

      // Determine the relevant event ID
      const id = this.elementSelected || eventId || ''

      // Handle extreme event loading
      if ((startDate || this.startDate) && !this.firstLoad && id && !this.loadExtremeEvent) {
        this.loadExtremeEvent = true
        this.changeStatingEndingDate(startDate ? new Date(startDate) : new Date(this.startDate), this.selectedView)
      }

      // Early exit if not force refreshing and employeeList is empty
      if (!forceRefresh && !this.employeeList.length) return

      // Filter employees by selected calendars
      const userArray = this.availableEmployees.filter(employee =>
        this.selectedCalendars.includes(employee.email.toLowerCase())
      )
      const userIds = userArray.map(employee => employee.id)

      // Get the key for the current calendar combination
      const calendarKey = this.getCalendarKey()

      // Get previously loaded range for this specific calendar combination
      const loadedRange = this.loadedRangesByCalendars[calendarKey]
      const oldRangeStart = loadedRange ? JSON.parse(JSON.stringify(loadedRange.rangeStart)) : null
      const oldRangeEnd = loadedRange ? JSON.parse(JSON.stringify(loadedRange.rangeEnd)) : null

      // Determine the date range to fetch
      // startDateFilter/endDateFilter represent the desired range (what should be loaded)
      let startDateLocale = this.startDateFilter
      let endDateLocale = this.endDateFilter

      if (this.isInitialLoad && !forceRefresh && this.selectedView !== 'year') {
        // On initial load, use only current period dates (optimization)
        const currentPeriod = this.getCurrentPeriodDates(
          this.getAnchorDateForEventLoad(startDate),
          this.selectedView
        )
        startDateLocale = currentPeriod.startDate
        endDateLocale = currentPeriod.endDate
      }
      // After initial load, always use startDateFilter/endDateFilter (full range: previous + current + next)

      // Determine if we need to fetch events
      // Compare the range we're about to fetch (startDateLocale/endDateLocale) with previously loaded range for this calendar combination
      // Fetch if: force refresh requested, initial load, no range loaded yet, or requested range extends beyond loaded range
      const shouldFetch = userArray.length > 0 && (
        forceRefresh ||
        this.isInitialLoad ||
        !oldRangeStart ||
        !oldRangeEnd ||
        this.dateObjectToTimeStamp(oldRangeStart) > this.dateObjectToTimeStamp(startDateLocale) ||
        this.dateObjectToTimeStamp(oldRangeEnd) < this.dateObjectToTimeStamp(endDateLocale)
      )

      if (!shouldFetch) return

      if (startDateLocale && endDateLocale) {
        try {
          const response = await CalendarApi.getListEvents(
            this.accountId,
            [],
            userIds,
            startDateLocale,
            endDateLocale
          )
          response.events.forEach(item => {
            // Remove duplicates
            this.dvEvents = this.dvEvents.filter(x =>
              !(this.getUniqueId(x) === this.getUniqueId(item) && item.userId === x.userId)
            )
            if (item.organizer?.email) {
              item.organizer.email = item.organizer?.email?.toLowerCase()
            }
            this.dvEvents.push(item)
            this.addEventToCalendarFormat(item)
          })
          // Ensure events from unselected calendars are removed even when API returns zero events
          this.events = this.events.filter(e => userIds.includes(e.userId))
          this.dvEvents = this.dvEvents.filter(e => userIds.includes(e.userId))
          this.updateEventCounts()

          // Update loaded range for this specific calendar combination
          // Expand the tracked range to include what was just fetched (union of ranges)
          if (this.isInitialLoad && !forceRefresh && this.selectedView !== 'year') {
            // Save the actual range that was fetched (current period only) for this calendar combination
            if (!this.loadedRangesByCalendars[calendarKey]) {
              this.loadedRangesByCalendars[calendarKey] = { rangeStart: null, rangeEnd: null }
            }
            this.loadedRangesByCalendars[calendarKey].rangeStart = JSON.parse(JSON.stringify(startDateLocale))
            this.loadedRangesByCalendars[calendarKey].rangeEnd = JSON.parse(JSON.stringify(endDateLocale))
            this.isInitialLoad = false
            // Set full range dates
            this.changeStatingEndingDate(
              this.getAnchorDateForEventLoad(startDate),
              'firstLoad'
            )
            // Load remaining events (previous and next periods) asynchronously
            // The saved range for this calendar combination will prevent fetching overlapping data
            this.$nextTick(() => {
              this.loadEvents(false, eventId, startDate)
            })
          } else {
            // For non-initial loads, expand the tracked range to include what was just fetched
            // This ensures we track the union of all fetched ranges for this calendar combination
            if (!this.loadedRangesByCalendars[calendarKey]) {
              this.loadedRangesByCalendars[calendarKey] = { rangeStart: null, rangeEnd: null }
            }
            const currentRange = this.loadedRangesByCalendars[calendarKey]
            if (!currentRange.rangeStart || this.dateObjectToTimeStamp(startDateLocale) < this.dateObjectToTimeStamp(currentRange.rangeStart)) {
              currentRange.rangeStart = JSON.parse(JSON.stringify(startDateLocale))
            }
            if (!currentRange.rangeEnd || this.dateObjectToTimeStamp(endDateLocale) > this.dateObjectToTimeStamp(currentRange.rangeEnd)) {
              currentRange.rangeEnd = JSON.parse(JSON.stringify(endDateLocale))
            }
          }

          const selectedId = this.elementSelected || eventId || ''
          if ((!this.firstLoad || startDate) && selectedId) {
            this.onEventClick(this.dvEvents.find(x => x.id === selectedId))
            this.firstLoad = true
            this.loadExtremeEvent = false
          }
        } catch (e) {
          console.error(e)
          Vue.toasted.show(this.$t('message.loadingErrors.calendar'), { type: 'error' })
        } finally {
          this.handleResize()
        }
      }
    },
    closeDetailEvent() {
      this.selectedEvent = null
      this.showCreationModal = false
      this.keyId = Math.random().toString(36).substr(2, 5)

      const query = Object.assign({}, this.$route.query)
      // Keep contact / schedule-viewing context when closing create-event modal.
      const isContactOrigin = query.fromPage === 'contact'
      const isScheduleViewingOrigin = query.fromPage === 'scheduleViewing'
      if (!isContactOrigin && !isScheduleViewingOrigin && (query.referenceIds || query.fromPage)) {
        delete query.referenceIds
        delete query.fromPage
        delete query.addMore
        this.$router.replace({ query: query })
      }
    },
    onScheduleViewingCancel() {
      this.$router.push({
        name: 'MarketingObjectDetailsView',
        params: { id: this.objectId || this.object?.id },
        query: { view: 3 }
      })
    },
    onScheduleViewingSaved(payload: { informContact?: boolean } = {}) {
      const query: Record<string, string | number> = { view: 3 }
      if (payload?.informContact) {
        query.informViewingContact = '1'
      }
      this.$router.push({
        name: 'MarketingObjectDetailsView',
        params: { id: this.objectId || this.object?.id },
        query
      })
    },
    getViewingContactRoleLabel(contactType: string) {
      switch (contactType) {
        case 'VIEWING_APPOINTMENT_CONTACT_TYPE_TENANT':
          return this.$t('message.generic.tenant').toString()
        case 'VIEWING_APPOINTMENT_CONTACT_TYPE_CARETAKER':
          return this.$t('message.contact.types.janitor').toString()
        case 'VIEWING_APPOINTMENT_CONTACT_TYPE_SERVICE_PROVIDER':
          return this.$t('message.contact.types.SERVICE_PROVIDER').toString()
        case 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT':
          return this.$t('message.contact.types.anyContact').toString()
        case 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE':
        default:
          return this.$t('message.contact.types.EMPLOYEE').toString()
      }
    },
    async resolveScheduleViewingContact() {
      this.scheduleViewingContact = null
      const viewingAppointment = this.object?.viewingAppointment as Record<string, string> | undefined
      if (!viewingAppointment) {
        return
      }
      const contactType = viewingAppointment.contactType || 'VIEWING_APPOINTMENT_CONTACT_TYPE_ASSIGNEE'
      const roleLabel = this.getViewingContactRoleLabel(contactType)
      const contacts = this.object?.contacts as Record<string, string> | undefined
      if (contactType === 'VIEWING_APPOINTMENT_CONTACT_TYPE_FREE_TEXT') {
        const displayName = [viewingAppointment.firstName, viewingAppointment.lastName]
          .filter(Boolean)
          .join(' ')
          .trim()
        const email = (viewingAppointment.email || '').trim()
        if (!displayName && !email) {
          return
        }
        this.scheduleViewingContact = {
          id: contacts?.contactIdViewingAppointment || '',
          displayName,
          roleLabel,
          email,
          contactType
        }
        return
      }
      const contactId = contacts?.contactIdViewingAppointment || ''
      if (!contactId) {
        return
      }
      try {
        const response = await ContactApi.contactResolve([contactId])
        const resolvedContacts = ([] as Array<{
          id?: string
          name?: string
          firstName?: string
          lastName?: string
          email?: string
          workEmail?: { email?: string }
          contactPersons?: Array<{
            id?: string
            name?: string
            firstName?: string
            lastName?: string
            email?: string
          }>
        }>).concat(response.persons as never[] || [], response.companies as never[] || [])
        const contact = resolvedContacts.find(c => c.id === contactId) ||
          resolvedContacts.find(c => (c.contactPersons || []).some(p => p.id === contactId))
        if (!contact) {
          return
        }
        let displayName = ''
        let email = ''
        if (contact.id === contactId) {
          displayName = contact.name ||
            [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim()
          email = (contact.email || contact.workEmail?.email || '').trim()
        } else {
          const person = (contact.contactPersons || []).find(p => p.id === contactId)
          displayName = person?.name ||
            [person?.firstName, person?.lastName].filter(Boolean).join(' ').trim() ||
            contact.name || ''
          email = (person?.email || contact.email || contact.workEmail?.email || '').trim()
        }
        if (!displayName) {
          return
        }
        this.scheduleViewingContact = {
          id: contactId,
          displayName,
          roleLabel,
          email,
          contactType
        }
      } catch (e) {
        console.log(e)
      }
    },
    onScheduleDraftSlotsChanged(drafts) {
      this.scheduleDraftSlots = drafts || []
      this.$nextTick(() => {
        this.updateColors()
      })
    },
    onScheduleFocusDateChanged(dateString) {
      const parsedFocusDate = this.parseDateValue(dateString)
      if (parsedFocusDate) {
        this.dayFocus = parsedFocusDate
        this.isPostCreationFocusActive = true
        this.changeStatingEndingDate(this.getFocusDateForRange(), this.selectedView, true)
        this.updateURLCalendarView(this.selectedCalendars)
      }
    },
    addEventToCalendarFormat(item) {
      // Cache employee lookup and color
      const organizerEmployee = this.employeeList.find(employee => employee.id === (item.userId || item.organizer?.id))
      const color = organizerEmployee?.profile?.color || ''

      // Cache user IDs filter
      const userIds = this.employeeList
        .filter(employee => this.selectedCalendars.includes(employee.email.toLowerCase()))
        .map(employee => employee.id)

      // Cache formatted dates and times for condition check
      const defaultTime = { hours: 0, minutes: 0, seconds: 0, nanos: 0 }
      const startTime = item.startTime || defaultTime
      const endTime = item.endTime || defaultTime
      const startDateFormatted = this.formatDateForCalendar(item.startDate, item.startTime)
      const endDateFormatted = this.formatDateForCalendar(item.endDate, item.endTime)

      // Determine if all-day event
      const isAllDay = startDateFormatted === endDateFormatted ||
                      this.formatOnlyTimeForCalendar(item.startTime) === this.formatOnlyTimeForCalendar(item.endTime) ||
                      (item.startTime === null && item.endTime === null)

      // Cache common computed values
      const isPrivate = item.visibility === 'EVENT_VISIBILITY_PRIVATE'
      const isPrivateForOtherUser = isPrivate && item.userId !== this.currentUserId
      const privateEventText = isPrivateForOtherUser ? this.$t('message.calendar.event.privateEvent') : null
      const colorClass = this.getColor(color)
      const durationClass = this.getEventsDuration(item, isAllDay)
      const hasMasterEvent = Boolean(item.masterEventId)
      const finalStartTime = isAllDay ? item.startTime : startTime
      const finalEndTime = isAllDay ? item.endTime : endTime
      const finalEndDate = isAllDay ? this.toObject(new Date(endDateFormatted)) : item.endDate

      // Build event object with common properties
      const event = {
        ...item,
        endDate: finalEndDate,
        startTime: finalStartTime,
        endTime: finalEndTime,
        url: item.url || '',
        private: isPrivate,
        recurrenceStartDate: item.recurrenceStartDate || null,
        recurrenceEndDate: item.recurrenceEndDate || null,
        occurrenceCount: item.occurrenceCount || 0,
        frequency: item.frequency || 'EVENT_FREQUENCY_UNDEFINED',
        allDay: isAllDay,
        start: this.formatDateForCalendar(item.startDate, finalStartTime),
        end: this.formatDateForCalendar(item.endDate, finalEndTime),
        title: isPrivateForOtherUser ? `<b>${this.escapeHtml(String(privateEventText))}</b>` : item.title,
        originalTitle: this.decodeHtmlEntitiesIfNeeded(privateEventText || item.title),
        contentFull: this.$options.filters.displayOptionalValue(item.description),
        class: `colorful-event color-${colorClass} ${durationClass}`,
        color,
        reminders: item.reminders || [],
        masterEventId: item.masterEventId || '',
        description: this.sanitizeHtml(item.description),
        recurringEvent: hasMasterEvent
      }

      // Remove existing event with same id/userId/occurrenceEventDate
      this.events = this.events.filter(x =>
        x.id !== item.id ||
        (x.userId !== item.userId && x.id === item.id) ||
        (x.id === item.id && x.occurrenceEventDate && item.occurrenceEventDate && !this.isSameDate(x.occurrenceEventDate, item.occurrenceEventDate))
      )

      // Add new event
      this.events.push(event)

      // Filter by user IDs
      this.events = this.events.filter(e => userIds?.includes(e.userId))

      // Sort events by start date (only needed for non-all-day events)
      if (!isAllDay) {
        this.events.sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      }

      this.updateColors()
    },
    isSameDate(date1, date2) {
      if (!date1 || !date2) {
        return false
      }
      return date1.year === date2.year && date1.month === date2.month && date1.day === date2.day
    },
    getDayNameWithDate(date) {
      const days = this.daysOftheWeek
      return `${this.$options.filters.formatDate(date.substr(0, 10))} - ${days[new Date(date).getDay()]}`
    },
    getEventsDuration(item, isWholeDay) {
      if (!isWholeDay) {
        const start = new Date(this.formatDateForCalendar(item.startDate, item.startTime))
        const end = new Date(this.formatDateForCalendar(item.endDate, item.endTime))
        const differenceInTime = end.getTime() - start.getTime()
        const differenceInMin = differenceInTime / (1000 * 60)
        return differenceInMin <= 15 ? 'xsmall-font-size' : ''
      } else {
        return ''
      }
    },
    createEvent(item, domEvent = null) {
      if (this.isScheduleViewingMode) {
        return
      }
      this.isAllDay = (domEvent?.srcElement as HTMLElement)?.parentElement?.parentNode?.parentElement?.classList?.contains('vuecal__all-day') || false
      this.defaultDate = item
      this.selectedEvent = null
      this.showCreationModal = true
      this.$nextTick(function () {
        if (!item || !item.private || (item.private && item.userId === this.currentUserId)) {
        this.$refs['modal-creation'].show()
        }
      })
    },
    sortedGroup(group) {
      return group.sort(function (a, b) {
        let results = 0
        if (a.startTime === null && b.startTime === null) {
          return 0
        } else if (a.startTime === null) {
          return 1
        } else if (b.startTime === null) {
          return -1
        }
        if (results === 0) {
          results = a.startTime.hours > b.startTime.hours ? 1 : a.startTime.hours < b.startTime.hours ? -1 : 0
        }
        if (results === 0) {
          results = a.startTime.minutes > b.startTime.minutes ? 1 : a.startTime.minutes < b.startTime.minutes ? -1 : 0
        }
        return results
      })
    },
    employeeSelected(employee) {
      if (!this.selectedCalendars.includes(employee.email.toLowerCase())) {
        this.selectedCalendars.push(employee.email.toLowerCase())
      } else {
        this.selectedCalendars = this.selectedCalendars.filter(entry => entry.toLowerCase() !== employee.email.toLowerCase())
      }
      this.updateColors()
    },
    formatOnlyTimeForCalendar(time) {
      return time ? ((time.hours.toString().length === 2 ? time.hours.toString() : '0' + time.hours.toString()) + ':' + (time.minutes.toString().length === 2 ? time.minutes.toString() : '0' + time.minutes.toString()) + ':00') : ''
    },
    getColor(color) {
      if (color) {
        let colorCss = '#'
        colorCss += color.red.toString(16).length > 1 ? color.red.toString(16) : '0' + color.red.toString(16)
        colorCss += color.green.toString(16).length > 1 ? color.green.toString(16) : '0' + color.green.toString(16)
        colorCss += color.blue.toString(16).length > 1 ? color.blue.toString(16) : '0' + color.blue.toString(16)
        colorCss += color.alpha !== null ? (color.alpha * 255).toString(16).substring(0, 2) : ''
        return colorCss
      } else {
        return ''
      }
    },
    onCalReady() {
      this.updateColors()
      this.scrollToSpecificTime()
    },
    scrollToSpecificTime() {
      if (!['day', 'week'].includes(this.selectedView)) {
        return
      }
      const calendar = document.querySelector('#globalCal .vuecal__bg')
      if (!calendar) {
        return
      }
      const calendarStartMinutes = 6 * 60
      const displayStartMinutes = 7 * 60 + 25
      const minutesFromStart = Math.max(displayStartMinutes - calendarStartMinutes, 0)
      const pixelsPerMinute = this.timeCellHeight / 60
      calendar.scrollTo({ top: minutesFromStart * pixelsPerMinute, behavior: 'auto' })
    },
    onDayClick(event) {
      this.selectedDate = event
    },
    onDayFocus(event) {
      this.dayFocus = event
      this.updateURLCalendarView(this.selectedCalendars)
    },
    onEventClick(event, e) {
      if (event?.isScheduleDraft) {
        if (e) {
          e.stopPropagation()
        }
        return
      }
      if (event) {
        this.keyId = Math.random().toString(36).substr(2, 5)
        // const emailsAttendees = event.attendees?.length > 0 ? event.attendees.map(x => x.email) : []
        // const isEmailAttendeesInCalendars = emailsAttendees.some(email => this.calendars.includes(email))
        // const checkExportedEvent = (!isEmailAttendeesInCalendars && !(event.organizer?.email && this.calendars.includes(event.organizer?.email))) || false
        // we search for the events with the current user or events for selected calendar or exported events
        // const list = this.dvEvents.filter(obj => this.getUniqueId(obj) === this.getUniqueId(event) && (obj.userId === this.userId || (obj.organizer?.email && this.calendars.includes(obj.organizer?.email)) || isEmailAttendeesInCalendars || checkExportedEvent))
        // we try to find the event for current user first and if we don't find then we search for the selected calendar then we search for the exported events
        // const selectedEvent = (list?.find(obj => obj.userId === this.userId) ? list?.find(obj => obj.userId === this.userId) : list?.find(obj => (obj.organizer?.email && this.calendars.includes(obj.organizer?.email)) || isEmailAttendeesInCalendars || checkExportedEvent)) || null
        // if (selectedEvent && !selectedEvent.endDateEdited && ((selectedEvent.endTime === null && selectedEvent.startTime === null) || JSON.stringify(selectedEvent.endTime) === JSON.stringify(selectedEvent.startTime) || (selectedEvent.startTime === null || (selectedEvent.startTime && selectedEvent.endTime && selectedEvent.startTime.hours === 0 && selectedEvent.startTime.minutes === 0 &&
        // selectedEvent.endTime.hours === 23 && selectedEvent.endTime.minutes === 59)))) {
        //   let endDate = new Date(this.formatDateForCalendar(selectedEvent.endDate, selectedEvent.endTime))
        //   endDate.setDate(endDate.getDate() - 1)
        //   endDate = this.toObject(endDate)
        //   selectedEvent.endDate = endDate
        //   selectedEvent.endDateEdited = true
        // }

        this.selectedEvent = event || null

        // reserved for recurrent events part
        // this.selectedEvent = this.dvEvents.find(obj => obj.url === event.url && JSON.stringify(obj.occurrenceEventDate) === JSON.stringify(event.occurrenceEventDate))

        this.$nextTick(function () {
          if (this.selectedEvent && (!event || !event.private || (event.private && event.userId === this.currentUserId))) {
            this.$refs['modal-creation']?.show()
          }
        })
      }
      // Prevent navigating to narrower view (default vue-cal behavior).
      if (e) {
        e.stopPropagation()
      }
    },
    onEventAgendaClick(event) {
      this.keyId = Math.random().toString(36).substr(2, 5)
      // const selectedEvent = this.dvEvents.find(obj => this.getUniqueId(obj) === this.getUniqueId(event) && (obj.userId === this.userId || (obj.organizer?.email && this.calendars.includes(obj.organizer?.email)))) || null
      if (event && !event.endDateEdited && ((event.endTime === null && event.startTime === null) || JSON.stringify(event.endTime) === JSON.stringify(event.startTime) || (event.startTime === null || (event.startTime.hours === 0 && event.startTime.minutes === 0 &&
        event.endTime.hours === 23 && event.endTime.minutes === 59)))) {
        let endDate = new Date(this.formatDateForCalendar(event.endDate, event.endTime))
        // endDate.setDate(endDate.getDate() - 1)
        endDate = this.toObject(endDate)
        event.endDate = endDate
        event.endDateEdited = true
      }
      this.selectedEvent = event

      // reserved for recurrent events part
      // this.selectedEvent = this.dvEvents.find(obj => obj.url === event.url && JSON.stringify(obj.occurrenceEventDate) === JSON.stringify(event.occurrenceEventDate))

      this.$nextTick(function () {
        if (!event || !event.private || (event.private && event.userId === this.currentUserId)) {
          this.$refs['modal-creation'].show()
        }
      })
    },
    onViewChange(view, e) {
      if (view !== this.selectedView) {
        return
      }
      if (this.isPostCreationFocusActive) {
        // Skip the first view-change immediately after a post-creation focus update
        // so the calendar stays anchored on the created event.
        this.isPostCreationFocusActive = false
        return
      }
      const startDate = e.startDate
      this.dayFocus = startDate
      this.updateURLCalendarView(this.selectedCalendars)
      this.changeStatingEndingDate(startDate, view)
      this.$nextTick(function () {
        this.loadEvents()
      })
      this.updateEventCounts()
      this.updateColors()
      this.handleResize()
      this.$nextTick(function () {
        this.scrollToSpecificTime()
      })
    },
    afterEdit() {
      this.$nextTick(function () {
        this.closeDetailEvent()
        this.loadEvents(true)
      })
    },
    getNameMainContact() {
      return this.contacts.find(x => x.id === this.referenceIds.split(',')[0])?.name
    },
    postCreation(items, bulk = false) {
      this.keyId = Math.random().toString(36).substr(2, 5)
      this.selectedEvent = null
      if (this.objectId !== '') {
        const requestId = this.referenceIds.split(',').find(element => element.indexOf('request_') !== -1)
        if (requestId) {
          RequestApi.partiallySetChecklist([requestId], { viewingAppointmentArranged: true })
            .then(() => {
              this.$router.push({
                name: 'MarketingObjectDetailsView',
                params: { id: this.objectId },
                query: { view: 0, elementSelected: this.referenceIds.split(',')[0] }
              })
            })
        } else {
          this.$router.push({
            name: 'MarketingObjectDetailsView',
            params: { id: this.objectId },
            query: { view: 0, elementSelected: this.referenceIds.split(',')[0] }
          })
        }
      } else if (this.referenceIds !== '') {
        if (this.isOwnerModule) {
          this.$router.push({
            name: 'OwnerContactDetailsView',
            params: { id: this.referenceIds.split(',').find(x => x.startsWith('contact')) }
          })
        } else if (this.isMarketingModule) {
          this.$router.push({
            name: 'MarketingContactDetailsView',
            params: { id: this.referenceIds.split(',').find(x => x.startsWith('contact')) }
          })
        } else if (this.isAccountingModule) {
          this.$router.push({
            name: 'AccountingContactDetailsView',
            params: { id: this.referenceIds.split(',').find(x => x.startsWith('contact')) }
          })
        } else if (this.isAssetModule) {
          this.$router.push({
            name: 'AssetContactDetailsView',
            params: { id: this.referenceIds.split(',').find(x => x.startsWith('contact')) }
          })
        } else {
          this.$router.push({
            name: 'AdminContactDetailsView',
            params: { id: this.referenceIds.split(',').find(x => x.startsWith('contact')) }
          })
        }
      }
      if (bulk) {
        this.dvEvents = this.dvEvents.filter(x => x.id !== items[0].id)
        this.events = this.events.filter(x => x.id !== items[0].id)
      }
      if (this.selectedView === 'list') {
        items.reverse().forEach(item => {
          this.events = this.events.filter(x => x.id !== item.id || (x.userId !== item.userId && x.id === item.id) || (x.id === item.id && x.occurrenceEventDate && item.occurrenceEventDate && !this.isSameDate(x.occurrenceEventDate, item.occurrenceEventDate)))
        })
      }
      // this.events = this.events.filter(x => x.id !== items[0].id)
      items.reverse().forEach(item => {
        this.dvEvents = this.dvEvents.filter(x => x.id !== item.id || (x.id === item.id && item.occurrenceEventDate && !this.isSameDate(x.occurrenceEventDate, item.occurrenceEventDate)))
        const isEmailExists = this.selectedCalendars.some(email =>
          item.attendees.some(attendee => attendee && attendee.email?.toLowerCase() === email?.toLowerCase())
        )
        if (
          this.selectedCalendars.includes(item.organizer?.email?.toLowerCase()) ||
          isEmailExists ||
          this.normalizeEmail(this.calendarOfUser?.associatedEmail) === this.normalizeEmail(item.organizer?.email)
        ) {
          this.addEventToCalendarFormat(item)
          this.dvEvents.push(item)
          this.updateColors()
          this.dayFocus = this.formatDateForCalendar(item.startDate, item.startTime)
          this.updateEventCounts()
          Vue.toasted.show(this.$t('message.successMessages.eventCreated'), { type: 'success' })
        } else {
          Vue.toasted.show(this.$t('message.successMessages.eventCreatedNotVisible'), { type: 'success' })
        }
      })

      // After creating an event, explicitly focus the calendar on the event date
      // and persist that focus into the URL. A transient guard prevents the next
      // vue-cal @view-change from overwriting this post-creation focus.
      if (items.length > 0) {
        const lastItem = items[0]
        const focusDateString = this.formatDateForCalendar(lastItem.startDate, lastItem.startTime)
        const parsedFocusDate = this.parseDateValue(focusDateString)
        if (parsedFocusDate) {
          this.dayFocus = parsedFocusDate
        }
        this.isPostCreationFocusActive = true
        this.changeStatingEndingDate(this.getFocusDateForRange(), this.selectedView, true)
        this.updateURLCalendarView(this.selectedCalendars)
      }

      const query = Object.assign({}, this.$route.query)
      if (query.referenceIds || query.fromPage) {
        const keepScheduleViewing = query.fromPage === 'scheduleViewing'
        const keepContact = query.fromPage === 'contact'
        if (query.referenceIds && !keepContact) {
          delete query.referenceIds
        }
        if (query.fromPage && !keepScheduleViewing && !keepContact) {
          delete query.fromPage
          delete query.addMore
        }
        this.$router.replace({ query: query })
      }
    },
    copyExportClicked(item) {
      this.$clipboard(item)
    },
    removeEvent(event, isSingle) {
      this.events = this.events.filter(x => isSingle ? x.id !== event.id || (x.id === event.id && event.occurrenceEventDate && !this.isSameDate(x.occurrenceEventDate, event.occurrenceEventDate)) : x.id !== event.id)
      this.updateColors()
    },
    selectAll(value) {
      this.sortedEmployeeList.forEach(employee => {
        if (value) {
          if (!this.selectedCalendars.includes(employee.email.toLowerCase())) {
            this.selectedCalendars.push(employee.email.toLowerCase())
          }
        } else {
          this.selectedCalendars = []
        }
      })
      const allCheckboxesComponents = this.$refs.checkbox
      if (allCheckboxesComponents) {
        allCheckboxesComponents.forEach(component => {
          const node = component.$el.childNodes[0]
          if (node.checked !== value) {
            node.checked = value
            let event = new Event('change')
            node.dispatchEvent(event)
          }
        })
      }
    },
    switchView(view, date = null) {
      this.selectedView = view
      if (date) {
        this.dayFocus = date
      }
      const anchorDate = date || this.getFocusDateForRange()
      this.changeStatingEndingDate(anchorDate, view)
      this.updateURLCalendarView(this.selectedCalendars)
      this.$nextTick(function () {
        this.loadEvents()
        this.$nextTick(function () {
          this.updateColors()
        })
      })
      if (this.$refs.globalCal && this.selectedView !== 'list') {
        this.$nextTick(function () {
          if (date) {
            this.$refs.globalCal.switchView(view, date)
          } else {
            this.$refs.globalCal.switchView(view)
          }
        })
      }
    },
    getEventDurationInMinutes(item) {
      if (!item?.startDate || !item?.endDate || !item?.startTime || !item?.endTime) {
        return null
      }
      const startDateString = this.formatDateForCalendar(item.startDate, item.startTime)
      const endDateString = this.formatDateForCalendar(item.endDate, item.endTime)
      if (!startDateString || !endDateString) {
        return null
      }
      const startDateTime = new Date(startDateString)
      const endDateTime = new Date(endDateString)
      if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
        return null
      }
      return (endDateTime.getTime() - startDateTime.getTime()) / (1000 * 60)
    },
    getFormattedEventTitle(item) {
      const hasTitle = typeof item?.title === 'string' && item.title.trim() !== ''
      const hasLocation = typeof item?.location === 'string' && item.location.trim() !== ''
      const durationInMinutes = this.getEventDurationInMinutes(item)
      const isLongEvent = durationInMinutes === null || durationInMinutes >= 60
      const shouldAppendLocation = hasLocation && (!hasTitle || isLongEvent)
      // Private events for other users already store title as escaped HTML `<b>...</b>`
      const isPrivateTitleHtml = Boolean(
        item?.private &&
        item.userId !== this.currentUserId &&
        typeof item.title === 'string' &&
        item.title.startsWith('<b>')
      )

      let title = ''
      if (hasTitle) {
        title = isPrivateTitleHtml
          ? item.title
          : `<b>${this.escapeHtml(this.decodeHtmlEntitiesIfNeeded(item.title))}</b>`
      }
      if (shouldAppendLocation && item.location !== '' && (!item.private || (item.private && item.userId === this.currentUserId))) {
        const escapedLocation = this.escapeHtml(this.decodeHtmlEntitiesIfNeeded(item.location))
        title += hasTitle ? `<br>${escapedLocation}` : escapedLocation
      }
      return this.$options.filters.displayOptionalValue(title)
    },
    // Add a helper method to calculate current period dates only (without previous/next)
    getCurrentPeriodDates(date, view) {
      let currentDate = null
      let startDate = null
      let endDate = null

      if (view === 'day') {
        if (date) {
          currentDate = date
        } else {
          currentDate = new Date()
        }
        startDate = this.toObject(new Date(currentDate))
        endDate = this.toObject(new Date(currentDate))
      } else if (view === 'week') {
        if (date) {
          currentDate = date
        } else {
          const now = new Date()
          const mondayTimeStamp = now.setDate(now.getDate() - now.getDay() + 1)
          currentDate = new Date(mondayTimeStamp)
        }
        // Get start of week (Monday) and end of week (Sunday)
        const weekStart = new Date(currentDate)
        weekStart.setDate(currentDate.getDate() - currentDate.getDay() + 1)
        const weekEnd = new Date(weekStart)
        weekEnd.setDate(weekStart.getDate() + 6)
        startDate = this.toObject(weekStart)
        endDate = this.toObject(weekEnd)
      } else if (view === 'month') {
        if (date) {
          currentDate = date
        } else {
          const now = new Date()
          currentDate = new Date(now.getFullYear(), now.getMonth(), 1)
        }
        const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
        startDate = this.toObject(monthStart)
        endDate = this.toObject(monthEnd)
      } else if (view === 'year') {
        if (date) {
          currentDate = date
        } else {
          const now = new Date()
          currentDate = new Date(now.getFullYear(), 0, 1)
        }
        const yearStart = new Date(currentDate.getFullYear(), 0, 1)
        const yearEnd = new Date(currentDate.getFullYear(), 11, 31)
        startDate = this.toObject(yearStart)
        endDate = this.toObject(yearEnd)
      } else if (view === 'list') {
        currentDate = new Date()
        startDate = this.toObject(currentDate)
        const nextMonth = new Date()
        nextMonth.setDate(currentDate.getDate() + 90)
        endDate = this.toObject(nextMonth)
      }

      return { startDate, endDate }
    },
    // Get a unique key for the current calendar combination
    getCalendarKey() {
      // Sort calendars to ensure consistent key regardless of selection order
      return [...this.selectedCalendars].sort().join(',')
    }
  }
})
</script>

<style lang="scss" scoped>

.employees-calendar {
  background: $color-bg-grey;
}

.contact-content label {
  font-size: 0.8rem;
  width: calc(100% - 36px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

:deep(.xsmall-font-size .vuecal__event-title) {
  font-size: 0.55rem !important;
  position: relative;
  top: -2px;
}

:deep(.xsmall-font-size) {
  border-width: 0;
}

.color-box {
  border-radius: 50%;
  border: 1px solid #00000045;
  float: left;
  height: 20px;
  width: 20px;
  font-size: 10px;
  line-height: 18px;
  text-align: center;

  &.text {
    float: none;
  }

  &.agenda {
    margin-top: 3px;
  }
}

:deep(.description-cell, .attendees-cell, .location-cell, .from-cell) {
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
:deep(.time-cell) {
  max-width: 125px;
}

:deep(.description-cell) {
  max-width: 500px;
}

:deep(.title-cell) {
  min-width: 125px;
  max-width: 200px;
}

:deep(.description-cell p), :deep(.title-cell div) {
  word-break: break-all;
}

:deep(button.vuecal__arrow) {
  box-shadow: none;
}

:deep(#globalCal .month-view .vuecal__cell[class*="vuecal__cell--day"]) {
  min-height: 100px;
}

:deep(.vuecal) {
  height: calc(100vh - 160px);
  background: $color-bg-white;

  .vuecal__event--all-day {
    min-height: 26px;
  }

  .vuecal__all-day {
    scrollbar-width: none;
  }

  .vuecal__bg {
    overflow: auto;
    overflow-x: hidden;
    scrollbar-width: none;
  }

  .vuecal__all-day {
    padding-right: 0;
    overflow-y: auto;
  }

  .vuecal__header {
    font-size: 0.8rem !important;

    span {
      font-size: 0.875rem !important;
    }
  }

  .vuecal__title-bar {
    background-color: $color-primary !important;
  }

  .vuecal__title {
    background-color: $color-primary !important;

    span {
      font-size: 1em !important;
    }

    button {
      box-shadow: none;
    }
  }

  .vuecal__title, .vuecal__title button, .vuecal__arrow {
    color: $color-text-white !important;

    .angle {
      margin-bottom: 2px;
    }
  }

  .month-view .vuecal__cell-events {
    margin-top: 25px;
  }

  .vuecal__cell-events-count {
    margin-top: 1px;
    background-color: $color-primary;
  }

  .vuecal__no-event, .weekday-label {
    @media screen and (max-width: 767px) {
      font-size: 70%;
    }
  }

  .vuecal__event {
    background-color: $color-primary;
    color: $color-text-white;
    cursor: pointer;
  }

  & .vuecal__event-title {
    font-size: 0.8rem !important;
    text-align: left;
  }

  &.vuecal--month-view .vuecal__event {
    display: flex;
  }

  &.vuecal--month-view .vuecal__event-title {
    order: 2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    padding: 0 2px 0 5px;
  }

  &.vuecal--week-view .vuecal__event-title {
    padding-left: 3px;
  }

  &.vuecal--month-view .vuecal__event-time {
    order: 1;
    white-space: nowrap;
    display: inline;
    padding-left: 5px;
  }

  .vuecal__event-time {
    display: none;
    font-size: .85em;
  }

  .vuecal__weekdays-headings {
    color: $color-text-grey;
  }

  .vuecal__menu li {
    border-bottom-color: $color-text-white;
    color: $color-text-white;
  }

  .vuecal__menu li.vuecal__view-btn--active {
    background-color: rgba(255, 255, 255, 0.15);
  }

  .vuecal__title-bar {
    height: 36px;
  }

  .vuecal__title {
    color: $color-text-grey-dark;
    background-color: $color-grey;
  }

  .vuecal__cell.vuecal__cell--today, .vuecal__cell.vuecal__cell--current {
    color: $color-primary;
  }

  .vuecal:not(.vuecal--day-view) .vuecal__cell.vuecal__cell--selected {
    background-color: rgba(235, 255, 245, 0.4);
  }

  .vuecal__cell.vuecal__cell--selected:before {
    border-color: rgba(66, 185, 131, 0.5);
  }
}

:deep(.small-calendar-container) {
  .vuecal {
    height: 315px;

    .vuecal__arrow {
      .angle {
        margin-bottom: 1px;
      }
    }
  }

  @media (min-width: 768px) {
    max-width: 200px;
  }
}

@media (min-width: 768px) {
  .calendar-header-row,
  .calendar-body-row {
    flex-wrap: nowrap;
  }

  .calendar-header-row .small-calendar-container,
  .calendar-body-row .small-calendar-container {
    flex: 0 0 200px;
    width: 200px;
    max-width: 200px;
  }

  .calendar-header-row .big-calendar-container,
  .calendar-body-row .big-calendar-container {
    flex: 1 1 0;
    width: auto;
    max-width: none;
    min-width: 0;
  }
}

.eventPopover {
  position: fixed;
  z-index: 1000;
  pointer-events: none;

  .content {
    border-radius: 5px;
    background: #ededed;
    color: black;
    font-size: 14px;
    min-width: 100px;
    width: fit-content;
    max-width: 720px;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .description-text {
    word-wrap: break-word;
  }
}

:deep(.vuecal__event.schedule-draft-event) {
  opacity: 0.85;
  border-style: dashed !important;
}

:deep(.vuecal__event.colorful-event) {
  background-color: white !important;
  padding: 1px;
  color: #515151 !important;
}

//background-color: #1f2223;
.container-event-month {
  width: 100%;
  border: 1px solid !important;
  color: #62d0ff;
  background-color: transparent !important;
  border-radius: 5px;
  height: 100%;
  overflow: hidden;
  word-wrap: normal;

  .event-title, .vuecal__event-time {
    padding-left: 7px;
    text-align: left;
    height: 100%;
    line-height: 21px;
  }

  .vuecal__event-time {
    margin-bottom: 0.5rem;
  }
}

.container-event-month.accepted {
  color: #FFFFFF;
  border: 0px !important;
  background-color: #0c57d3 !important;
}

.container-event-month.declined {
  text-decoration: line-through;
}

:deep(.vuecal__cell .container_cell_year_info) {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;

  .vuecal__cell-date.year {
    flex: 1;
    max-height: 30px;
  }
}

:deep(.vuecal__header .vuecal__weekdays-headings *) {
  pointer-events: none;
}

.adjustWidthEditIcon {
  width: 14px;
  height: 14px;
  margin-bottom: 4px;
  margin-right: 5px;
}

:deep(.description-cell table > tbody > tr td) {
  border: 1px solid rgba(0, 0, 0, 0.125) !important;
}
:deep(.vuecal .vuecal__cell.vuecal__cell--selected:before) {
  border-color: #ddd !important;
}

.today-btn {
  padding: 0.25rem 1rem;
  color: #333 !important;
  background-color: #dfdfdf !important;
  border-color: #dfdfdf !important;
}

@media (max-width: 767px) {
  .big-calendar-container .btn-container .today-btn {
    display: block !important;
  }
  .big-calendar-container .btn-container .btn-group {
    width: 100% !important;
  }
}
@media (max-width: 576px) {
  .big-calendar-container .btn-container .today-btn {
    font-size: 0.8rem !important;
  }
  .big-calendar-container .btn-container .btn-group .btn {
    font-size: 0.8rem !important;
  }
}
</style>
